/**
 * SiteDatabaseTab
 * Displays all closed-won projects in the Site Database
 */

import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { siteDatabaseAPI, usersAPI } from '../../../services/api';
import { SITE_STATUS } from '../../../utils/constants';
import { toTitleCase } from '../../../utils/formatters';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { useAuth } from '../../../context/AuthContext';
import Pagination from '../../common/Pagination';
import BottomSheet from '../../common/BottomSheet';
import ResponsiveModal from '../../common/ResponsiveModal';
import AddSiteEntryWizard from '../../site/AddSiteEntryWizard';

// Site status display config
const SITE_STATUS_CONFIG = {
  [SITE_STATUS.PENDING_SS_ASSIGNMENT]: { label: 'Pending SS Assignment', shortLabel: 'Pending SS', color: 'bg-yellow-100 text-yellow-800', progressColor: 'bg-yellow-500' },
  [SITE_STATUS.ACTIVE]: { label: 'Active', shortLabel: 'Active', color: 'bg-blue-100 text-blue-800', progressColor: 'bg-blue-600' },
  [SITE_STATUS.ON_HOLD]: { label: 'On Hold', shortLabel: 'On Hold', color: 'bg-gray-100 text-gray-800', progressColor: 'bg-gray-400' },
  [SITE_STATUS.COMPLETED]: { label: 'Completed', shortLabel: 'Completed', color: 'bg-green-100 text-green-800', progressColor: 'bg-green-600' }
};

// Format currency
function formatCurrency(amount) {
  if (!amount) return 'N/A';
  return new Intl.NumberFormat('en-MY', {
    style: 'currency',
    currency: 'MYR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
}

// Format date as "22 Apr 2026"
function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-MY', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }).format(d);
}

const SITE_STATUS_ORDER = ['pending_ss_assignment', 'active', 'on_hold', 'completed'];

const SORTABLE_FIELDS = [
  { key: 'projectNumber', label: 'Project' },
  { key: 'clientName', label: 'Client' },
  { key: 'propertyType', label: 'Property' },
  { key: 'finalPackagePrice', label: 'Package Price' },
  { key: 'sidName', label: 'SID/ASID' },
  { key: 'siteSupervisorName', label: 'SS' },
  { key: 'overallProgress', label: 'Progress' },
  { key: 'siteStatus', label: 'Status' },
  { key: 'createdAt', label: 'Created Date' }
];

function getSortValue(entry, field) {
  switch (field) {
    case 'projectNumber':
      return entry.projectNumber || '';
    case 'clientName':
      return toTitleCase(entry.clientName) || '';
    case 'propertyType':
      return entry.propertyType || '';
    case 'finalPackagePrice':
      return parseFloat(entry.finalPackagePrice) || 0;
    case 'sidName':
      return entry.sidName || entry.asidName || '';
    case 'siteSupervisorName':
      return entry.siteSupervisorName || '';
    case 'overallProgress':
      return parseFloat(entry.overallProgress) || 0;
    case 'siteStatus': {
      const idx = SITE_STATUS_ORDER.indexOf(entry.siteStatus);
      return idx === -1 ? 999 : idx;
    }
    case 'createdAt':
      return entry.createdAt ? new Date(entry.createdAt).getTime() : 0;
    default:
      return '';
  }
}

function SortIcon({ field, sortField, sortDirection }) {
  const isActive = sortField === field;
  return (
    <span className="ml-1 inline-flex flex-col">
      <svg
        className={`w-2 h-2 ${isActive && sortDirection === 'asc' ? 'text-blue-600' : 'text-gray-300'}`}
        fill="currentColor"
        viewBox="0 0 8 4"
      >
        <path d="M4 0L8 4H0L4 0Z" />
      </svg>
      <svg
        className={`w-2 h-2 -mt-0.5 ${isActive && sortDirection === 'desc' ? 'text-blue-600' : 'text-gray-300'}`}
        fill="currentColor"
        viewBox="0 0 8 4"
      >
        <path d="M4 4L0 0H8L4 4Z" />
      </svg>
    </span>
  );
}

const CAN_ADD_SITE_ENTRY = ['system_admin', 'admin', 'director', 'manager', 'operation_head'];
const CAN_EDIT_PACKAGE_PRICE = ['system_admin', 'admin', 'director'];

export default function SiteDatabaseTab() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);

  // Package price edit (director access, for data cleanup)
  const canEditPackagePrice = CAN_EDIT_PACKAGE_PRICE.includes(currentUser?.role);
  const [priceEditEntry, setPriceEditEntry] = useState(null);
  const [priceInput, setPriceInput] = useState('');
  const [priceSaving, setPriceSaving] = useState(false);
  const [priceError, setPriceError] = useState(null);

  const openPriceEdit = (entry) => {
    setPriceEditEntry(entry);
    setPriceInput(entry.finalPackagePrice != null ? String(entry.finalPackagePrice) : '');
    setPriceError(null);
  };

  const closePriceEdit = () => {
    if (priceSaving) return;
    setPriceEditEntry(null);
    setPriceInput('');
    setPriceError(null);
  };

  const savePrice = async () => {
    const parsed = parseFloat(priceInput);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setPriceError('Enter a non-negative number');
      return;
    }
    try {
      setPriceSaving(true);
      setPriceError(null);
      await siteDatabaseAPI.updatePackagePrice(priceEditEntry.id, parsed);
      setPriceEditEntry(null);
      setPriceInput('');
      await fetchEntries();
    } catch (err) {
      setPriceError(err.response?.data?.error || err.message || 'Failed to update');
    } finally {
      setPriceSaving(false);
    }
  };

  // Filter state
  const [filters, setFilters] = useState({
    // Quick filters
    search: '',
    siteStatus: '',
    projectType: '',
    // Advanced filters
    propertyType: '',
    city: '',
    state: '',
    priceMin: '',
    priceMax: '',
    sidAssignment: '',
    siteSupervisor: '',
    siteManager: '',
    progressMin: '',
    progressMax: ''
  });
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Pagination state
  const [pageSize, setPageSize] = useLocalStorage('siteDatabasePageSize', 25);
  const [currentPage, setCurrentPage] = useState(1);

  // Sort state — default: newest first
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Scroll the list back into view when the user changes pages via pagination.
  // Only on explicit page changes — not on filter/sort-induced page resets (they
  // happen near the top anyway).
  const listTopRef = useRef(null);
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    listTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Mobile filter drawer state
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width: 767px)');

  // Active + resigned site staff roster, used to source the manager/supervisor
  // filter dropdowns (entries only carry name snapshots, with no resignation info).
  const [siteStaff, setSiteStaff] = useState([]);

  // Fetch data
  useEffect(() => {
    fetchEntries();
    fetchSiteStaff();
  }, []);

  async function fetchEntries() {
    try {
      setLoading(true);
      const response = await siteDatabaseAPI.getAll();
      setEntries(response.data.data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching site database:', err);
      setError(err.message || 'Failed to load site database');
    } finally {
      setLoading(false);
    }
  }

  async function fetchSiteStaff() {
    try {
      const response = await usersAPI.getByRoles(
        ['site_manager', 'site_supervisor'],
        { includeResigned: true }
      );
      setSiteStaff(response.data.data || []);
    } catch (err) {
      console.error('Error fetching site staff for filters:', err);
    }
  }

  // Extract unique filter options from entries (+ staff roster)
  const filterOptions = useMemo(() => {
    const propertyTypesSet = new Set();
    const citiesSet = new Set();
    const statesSet = new Set();
    const sidNamesSet = new Set();

    (entries || []).forEach(entry => {
      if (entry.propertyType) propertyTypesSet.add(entry.propertyType);
      if (entry.city) citiesSet.add(entry.city);
      if (entry.state) statesSet.add(entry.state);
      if (entry.sidName) sidNamesSet.add(entry.sidName);
      if (entry.asidName) sidNamesSet.add(entry.asidName);
    });

    // Build a staff dropdown list ({ name, isResigned }) from the active roster.
    // The stored snapshot name is `nickname || fullName` (see siteDatabaseService),
    // so we key on the same value. Resigned staff — and orphaned snapshot names no
    // longer in the roster — are included only when they still appear on an entry,
    // so selecting them returns results, and are flagged so the UI can mark them.
    const buildStaffOptions = (roster, entryNameKey) => {
      const byName = new Map(); // name -> isResigned
      roster.forEach(u => {
        const name = u.nickname || u.fullName;
        if (name && !u.isResigned) byName.set(name, false);
      });

      const assignedNames = new Set();
      (entries || []).forEach(e => {
        if (e[entryNameKey]) assignedNames.add(e[entryNameKey]);
      });

      roster.forEach(u => {
        const name = u.nickname || u.fullName;
        if (name && u.isResigned && assignedNames.has(name) && !byName.has(name)) {
          byName.set(name, true);
        }
      });
      assignedNames.forEach(name => {
        if (!byName.has(name)) byName.set(name, true); // snapshot name not in roster
      });

      return Array.from(byName, ([name, isResigned]) => ({ name, isResigned }))
        .sort((a, b) =>
          a.isResigned === b.isResigned ? a.name.localeCompare(b.name) : a.isResigned ? 1 : -1
        );
    };

    const managerRoster = siteStaff.filter(u => u.role === 'site_manager');
    const supervisorRoster = siteStaff.filter(u => u.role === 'site_supervisor');

    return {
      propertyTypes: Array.from(propertyTypesSet).sort(),
      cities: Array.from(citiesSet).sort(),
      states: Array.from(statesSet).sort(),
      sidNames: Array.from(sidNamesSet).sort(),
      siteSupervisors: buildStaffOptions(supervisorRoster, 'siteSupervisorName'),
      siteManagers: buildStaffOptions(managerRoster, 'siteManagerName')
    };
  }, [entries, siteStaff]);

  // Format property type for display
  const formatPropertyType = (type) => {
    if (!type) return 'N/A';
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Filter entries
  const filteredEntries = useMemo(() => {
    let result = [...entries];

    // Quick Filters
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(e =>
        e.projectNumber?.toLowerCase().includes(searchLower) ||
        e.clientName?.toLowerCase().includes(searchLower) ||
        e.clientEmail?.toLowerCase().includes(searchLower)
      );
    }

    if (filters.siteStatus) {
      result = result.filter(e => e.siteStatus === filters.siteStatus);
    }

    if (filters.projectType) {
      result = result.filter(e => e.projectType === filters.projectType);
    }

    // Advanced Filters
    if (filters.propertyType) {
      result = result.filter(e => e.propertyType === filters.propertyType);
    }

    if (filters.city) {
      result = result.filter(e => e.city === filters.city);
    }

    if (filters.state) {
      result = result.filter(e => e.state === filters.state);
    }

    if (filters.priceMin) {
      const min = parseFloat(filters.priceMin);
      result = result.filter(e => (parseFloat(e.finalPackagePrice) || 0) >= min);
    }

    if (filters.priceMax) {
      const max = parseFloat(filters.priceMax);
      result = result.filter(e => (parseFloat(e.finalPackagePrice) || 0) <= max);
    }

    if (filters.sidAssignment) {
      if (filters.sidAssignment === 'unassigned') {
        result = result.filter(e => !e.sidName && !e.asidName);
      } else if (filters.sidAssignment === 'assigned') {
        result = result.filter(e => e.sidName || e.asidName);
      } else {
        result = result.filter(e => e.sidName === filters.sidAssignment || e.asidName === filters.sidAssignment);
      }
    }

    if (filters.siteSupervisor) {
      if (filters.siteSupervisor === 'unassigned') {
        result = result.filter(e => !e.siteSupervisorName);
      } else if (filters.siteSupervisor === 'assigned') {
        result = result.filter(e => !!e.siteSupervisorName);
      } else {
        result = result.filter(e => e.siteSupervisorName === filters.siteSupervisor);
      }
    }

    if (filters.siteManager) {
      if (filters.siteManager === 'unassigned') {
        result = result.filter(e => !e.siteManagerName);
      } else if (filters.siteManager === 'assigned') {
        result = result.filter(e => !!e.siteManagerName);
      } else {
        result = result.filter(e => e.siteManagerName === filters.siteManager);
      }
    }

    if (filters.progressMin) {
      const min = parseFloat(filters.progressMin);
      result = result.filter(e => (parseFloat(e.overallProgress) || 0) >= min);
    }

    if (filters.progressMax) {
      const max = parseFloat(filters.progressMax);
      result = result.filter(e => (parseFloat(e.overallProgress) || 0) <= max);
    }

    return result;
  }, [entries, filters]);

  // Reset to page 1 when filters or sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, sortField, sortDirection]);

  // Sort filtered entries
  const sortedEntries = useMemo(() => {
    const list = [...filteredEntries];
    list.sort((a, b) => {
      const aVal = getSortValue(a, sortField);
      const bVal = getSortValue(b, sortField);
      let cmp = 0;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        cmp = aVal - bVal;
      } else {
        cmp = String(aVal).localeCompare(String(bVal));
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [filteredEntries, sortField, sortDirection]);

  // Paginate sorted entries
  const paginatedEntries = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return sortedEntries.slice(startIndex, startIndex + pageSize);
  }, [sortedEntries, currentPage, pageSize]);

  // Cascade filter: Site Supervisor options filtered by selected Site Manager
  const filteredSupervisorOptions = useMemo(() => {
    if (!filters.siteManager || filters.siteManager === 'assigned' || filters.siteManager === 'unassigned') {
      // No specific manager selected - show all supervisors
      return filterOptions.siteSupervisors;
    }
    // Filter supervisors to only those appearing in entries with this manager
    const supervisorsUnderManager = new Set();
    entries.forEach(entry => {
      if (entry.siteManagerName === filters.siteManager && entry.siteSupervisorName) {
        supervisorsUnderManager.add(entry.siteSupervisorName);
      }
    });
    return filterOptions.siteSupervisors.filter(opt => supervisorsUnderManager.has(opt.name));
  }, [entries, filters.siteManager, filterOptions.siteSupervisors]);

  // Handle filter change with cascade logic
  const handleFilterChange = (field, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [field]: value };
      // Cascade: clear supervisor when manager changes
      if (field === 'siteManager') {
        newFilters.siteSupervisor = '';
      }
      return newFilters;
    });
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      search: '',
      siteStatus: '',
      projectType: '',
      propertyType: '',
      city: '',
      state: '',
      priceMin: '',
      priceMax: '',
      sidAssignment: '',
      siteSupervisor: '',
      siteManager: '',
      progressMin: '',
      progressMax: ''
    });
  };

  // Handle page size change
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return Object.values(filters).some(v => v !== '');
  }, [filters]);

  // Check if any advanced filters are active (SM/SS are now quick filters)
  const hasActiveAdvancedFilters = useMemo(() => {
    const advancedFields = [
      'projectType', 'propertyType', 'city', 'state',
      'priceMin', 'priceMax', 'sidAssignment', 'progressMin', 'progressMax'
    ];
    return advancedFields.some(field => filters[field] !== '');
  }, [filters]);

  // Count active filters for mobile badge
  const activeFilterCount = useMemo(() => {
    return Object.values(filters).filter(v => v !== '').length;
  }, [filters]);

  // Handle row click - navigate to site detail page
  const handleRowClick = (entry) => {
    if (entry.id) {
      navigate(`/sites/${entry.id}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-sm text-red-600">{error}</p>
        <button
          onClick={fetchEntries}
          className="mt-2 text-sm text-red-700 hover:text-red-800 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header with Add button */}
      {CAN_ADD_SITE_ENTRY.includes(currentUser?.role) && (
        <div className="flex justify-end mb-4">
          <button
            onClick={() => setWizardOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-700 active:scale-95 transition-all"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Site Entry
          </button>
        </div>
      )}

      {/* Stats Summary - Horizontal scroll on mobile, grid on desktop */}
      <div className="flex md:grid md:grid-cols-5 gap-3 mb-6 overflow-x-auto pb-2 md:pb-0 snap-x snap-mandatory md:snap-none scrollbar-hide">
        {/* Total Sites */}
        <div className="bg-white rounded-lg border border-gray-200 p-3 md:p-4 flex-shrink-0 w-[140px] md:w-auto snap-start">
          <div className="text-xs md:text-sm font-medium text-gray-500 mb-1">Total Sites</div>
          <div className="text-xl md:text-2xl font-bold text-gray-900">{entries.length}</div>
        </div>

        {/* Status Cards */}
        {Object.entries(SITE_STATUS_CONFIG).map(([status, config]) => {
          const count = entries.filter(e => e.siteStatus === status).length;
          const isSelected = filters.siteStatus === status;
          return (
            <div
              key={status}
              className={`bg-white rounded-lg border-2 p-3 md:p-4 cursor-pointer active:scale-95 md:hover:shadow-md transition-all flex-shrink-0 w-[140px] md:w-auto snap-start ${
                isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'
              }`}
              onClick={() => handleFilterChange('siteStatus', isSelected ? '' : status)}
            >
              <div className={`inline-block px-1.5 md:px-2 py-1 rounded text-xs font-medium ${config.color} mb-2`}>
                <span className="md:hidden">{config.shortLabel}</span>
                <span className="hidden md:inline">{config.label}</span>
              </div>
              <div className="text-xl md:text-2xl font-bold text-gray-900">{count}</div>
            </div>
          );
        })}
      </div>

      {/* Filters Card - Desktop Only */}
      {!isMobile && (
      <div className="card mb-6">
        {/* Header with toggle and clear */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-gray-700">Filters</h3>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="text-sm text-blue-600 hover:text-blue-800 transition-colors flex items-center gap-1"
            >
              <svg
                className={`w-4 h-4 transition-transform duration-200 ${showAdvanced ? 'rotate-180' : ''}`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
              {showAdvanced ? 'Hide' : 'Show'} Advanced
              {hasActiveAdvancedFilters && (
                <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-600">
                  Active
                </span>
              )}
            </button>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Clear all
              </button>
            )}
          </div>
        </div>

        {/* Quick Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          {/* Search */}
          <div className="sm:col-span-2 md:col-span-1">
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Search</label>
            <input
              type="text"
              placeholder="Project number, client name, email..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full px-3 py-2.5 md:py-2 text-base md:text-sm border border-gray-300 rounded-lg md:rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Status filter */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Site Status</label>
            <select
              value={filters.siteStatus}
              onChange={(e) => handleFilterChange('siteStatus', e.target.value)}
              className="w-full px-3 py-2.5 md:py-2 text-base md:text-sm border border-gray-300 rounded-lg md:rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="">All Statuses</option>
              {Object.entries(SITE_STATUS_CONFIG).map(([status, config]) => (
                <option key={status} value={status}>
                  {config.label}
                </option>
              ))}
            </select>
          </div>

          {/* Site Manager filter */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Site Manager</label>
            <select
              value={filters.siteManager}
              onChange={(e) => handleFilterChange('siteManager', e.target.value)}
              className="w-full px-3 py-2.5 md:py-2 text-base md:text-sm border border-gray-300 rounded-lg md:rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="">All</option>
              <option value="assigned">Assigned (Any)</option>
              <option value="unassigned">Unassigned</option>
              {filterOptions.siteManagers.map(opt => (
                <option key={opt.name} value={opt.name}>
                  {opt.isResigned ? `${opt.name} (resigned)` : opt.name}
                </option>
              ))}
            </select>
          </div>

          {/* Site Supervisor filter - CASCADE filtered */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Site Supervisor</label>
            <select
              value={filters.siteSupervisor}
              onChange={(e) => handleFilterChange('siteSupervisor', e.target.value)}
              className="w-full px-3 py-2.5 md:py-2 text-base md:text-sm border border-gray-300 rounded-lg md:rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="">All</option>
              <option value="assigned">Assigned (Any)</option>
              <option value="unassigned">Unassigned</option>
              {filteredSupervisorOptions.map(opt => (
                <option key={opt.name} value={opt.name}>
                  {opt.isResigned ? `${opt.name} (resigned)` : opt.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Advanced Filters (Collapsible) */}
        {showAdvanced && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 pt-4 mt-4 border-t border-gray-200">
            {/* Project Type - moved from quick filters */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Project Type</label>
              <select
                value={filters.projectType}
                onChange={(e) => handleFilterChange('projectType', e.target.value)}
                className="w-full px-3 py-2.5 md:py-2 text-base md:text-sm border border-gray-300 rounded-lg md:rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">All Types</option>
                <option value="renovation">Renovation</option>
                <option value="build_from_land">Build from Land</option>
              </select>
            </div>

            {/* Property Type */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Property Type</label>
              <select
                value={filters.propertyType}
                onChange={(e) => handleFilterChange('propertyType', e.target.value)}
                className="w-full px-3 py-2.5 md:py-2 text-base md:text-sm border border-gray-300 rounded-lg md:rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">All Property Types</option>
                {filterOptions.propertyTypes.map(type => (
                  <option key={type} value={type}>{formatPropertyType(type)}</option>
                ))}
              </select>
            </div>

            {/* City */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">City</label>
              <select
                value={filters.city}
                onChange={(e) => handleFilterChange('city', e.target.value)}
                className="w-full px-3 py-2.5 md:py-2 text-base md:text-sm border border-gray-300 rounded-lg md:rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">All Cities</option>
                {filterOptions.cities.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>

            {/* State */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">State</label>
              <select
                value={filters.state}
                onChange={(e) => handleFilterChange('state', e.target.value)}
                className="w-full px-3 py-2.5 md:py-2 text-base md:text-sm border border-gray-300 rounded-lg md:rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">All States</option>
                {filterOptions.states.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>

            {/* Min Price */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Min Price (RM)</label>
              <input
                type="number"
                inputMode="numeric"
                placeholder="0"
                value={filters.priceMin}
                onChange={(e) => handleFilterChange('priceMin', e.target.value)}
                className="w-full px-3 py-2.5 md:py-2 text-base md:text-sm border border-gray-300 rounded-lg md:rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Max Price */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Max Price (RM)</label>
              <input
                type="number"
                inputMode="numeric"
                placeholder="No limit"
                value={filters.priceMax}
                onChange={(e) => handleFilterChange('priceMax', e.target.value)}
                className="w-full px-3 py-2.5 md:py-2 text-base md:text-sm border border-gray-300 rounded-lg md:rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* SID/ASID Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">SID/ASID</label>
              <select
                value={filters.sidAssignment}
                onChange={(e) => handleFilterChange('sidAssignment', e.target.value)}
                className="w-full px-3 py-2.5 md:py-2 text-base md:text-sm border border-gray-300 rounded-lg md:rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">All</option>
                <option value="assigned">Assigned (Any)</option>
                <option value="unassigned">Unassigned</option>
                {filterOptions.sidNames.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>

            {/* Progress Range */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Progress Range (%)</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  inputMode="numeric"
                  placeholder="0"
                  min="0"
                  max="100"
                  value={filters.progressMin}
                  onChange={(e) => handleFilterChange('progressMin', e.target.value)}
                  className="w-full px-3 py-2.5 md:py-2 text-base md:text-sm border border-gray-300 rounded-lg md:rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
                <span className="text-gray-400">-</span>
                <input
                  type="number"
                  inputMode="numeric"
                  placeholder="100"
                  min="0"
                  max="100"
                  value={filters.progressMax}
                  onChange={(e) => handleFilterChange('progressMax', e.target.value)}
                  className="w-full px-3 py-2.5 md:py-2 text-base md:text-sm border border-gray-300 rounded-lg md:rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Results Summary */}
      <div className="mb-4 text-sm text-gray-600">
        {filteredEntries.length > 0 ? (
          <>
            Showing {Math.min((currentPage - 1) * pageSize + 1, filteredEntries.length)}-{Math.min(currentPage * pageSize, filteredEntries.length)} of {filteredEntries.length} site projects
            {hasActiveFilters && ' (filtered)'}
          </>
        ) : (
          <>
            Showing 0 of {entries.length} site projects
            {hasActiveFilters && ' (filtered)'}
          </>
        )}
      </div>

      {/* Empty State */}
      {filteredEntries.length === 0 ? (
        <div className="card">
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No site projects</h3>
            <p className="mt-1 text-sm text-gray-500">
              {hasActiveFilters
                ? 'No projects match the current filters'
                : 'Projects will appear here when they are won'}
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Scroll anchor — Pagination scrolls here on page change */}
          <div ref={listTopRef} className="scroll-mt-4" />

          {/* Mobile Sort Bar */}
          <div className="md:hidden mb-3 flex items-center gap-2">
            <label htmlFor="mobile-sort-field" className="text-xs font-medium text-gray-600">Sort by</label>
            <select
              id="mobile-sort-field"
              value={sortField}
              onChange={(e) => { setSortField(e.target.value); setSortDirection('asc'); }}
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {SORTABLE_FIELDS.map(({ key, label }) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))}
              className="px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50"
              title={sortDirection === 'asc' ? 'Ascending — tap to flip' : 'Descending — tap to flip'}
              aria-label={`Sort direction: ${sortDirection === 'asc' ? 'ascending' : 'descending'}`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {sortDirection === 'asc' ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                )}
              </svg>
            </button>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {paginatedEntries.map((entry) => {
              const statusConfig = SITE_STATUS_CONFIG[entry.siteStatus] || {
                label: entry.siteStatus,
                color: 'bg-gray-100 text-gray-800',
                progressColor: 'bg-gray-400'
              };
              const progress = parseFloat(entry.overallProgress) || 0;

              return (
                <div
                  key={entry.id}
                  onClick={() => handleRowClick(entry)}
                  className="bg-white rounded-lg border border-gray-200 p-4 cursor-pointer active:bg-gray-50 transition-colors"
                >
                  {/* Card Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="text-sm font-semibold text-gray-900">
                        {entry.projectNumber}
                        <span className="ml-2 text-xs font-normal text-gray-400">&bull; {formatDate(entry.createdAt)}</span>
                      </div>
                      <div className="text-xs text-gray-500">
                        {entry.projectType === 'build_from_land' ? 'Build from Land' : 'Renovation'}
                      </div>
                    </div>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${statusConfig.color}`}>
                      {statusConfig.label}
                    </span>
                  </div>

                  {/* Client & Property */}
                  <div className="mb-3">
                    <div className="text-sm font-medium text-gray-900 line-clamp-1">{toTitleCase(entry.clientName)}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {entry.propertyType?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A'}
                      {(entry.city || entry.state) && (
                        <span> &bull; {entry.city || ''}{entry.city && entry.state ? ', ' : ''}{entry.state || ''}</span>
                      )}
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                      <span>Progress</span>
                      <span className="font-medium text-gray-700">{progress.toFixed(0)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${statusConfig.progressColor}`}
                        style={{ width: `${Math.min(progress, 100)}%` }}
                      />
                    </div>
                  </div>

                  {/* Footer Info */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium text-gray-900">
                        {formatCurrency(entry.finalPackagePrice)}
                      </span>
                      {canEditPackagePrice && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); openPriceEdit(entry); }}
                          className="p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                          title="Edit package price"
                          aria-label="Edit package price"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 text-right">
                      {entry.sidName || entry.asidName ? (
                        <span className="text-gray-700">{entry.sidName || entry.asidName}</span>
                      ) : (
                        <span className="text-gray-400">No SID</span>
                      )}
                      {entry.siteSupervisorName && (
                        <span className="block text-gray-500">SS: {entry.siteSupervisorName}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {SORTABLE_FIELDS.map(({ key, label }) => (
                      <th
                        key={key}
                        onClick={() => handleSort(key)}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      >
                        <div className="flex items-center">
                          {label}
                          <SortIcon field={key} sortField={sortField} sortDirection={sortDirection} />
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedEntries.map((entry) => {
                    const statusConfig = SITE_STATUS_CONFIG[entry.siteStatus] || {
                      label: entry.siteStatus,
                      color: 'bg-gray-100 text-gray-800',
                      progressColor: 'bg-gray-400'
                    };
                    const progress = parseFloat(entry.overallProgress) || 0;

                    return (
                      <tr
                        key={entry.id}
                        onClick={() => handleRowClick(entry)}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{entry.projectNumber}</div>
                          <div className="text-xs text-gray-500">
                            {entry.projectType === 'build_from_land' ? 'Build' : 'Renovation'}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm font-medium text-gray-900 max-w-[120px] line-clamp-2">{toTitleCase(entry.clientName)}</div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm text-gray-900">
                            {entry.propertyType?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {entry.city || entry.state ? `${entry.city || ''}${entry.city && entry.state ? ', ' : ''}${entry.state || ''}` : 'N/A'}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap group/price">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-medium text-gray-900">
                              {formatCurrency(entry.finalPackagePrice)}
                            </span>
                            {canEditPackagePrice && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); openPriceEdit(entry); }}
                                className="opacity-0 group-hover/price:opacity-100 transition-opacity p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                                title="Edit package price"
                                aria-label="Edit package price"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {entry.sidName || entry.asidName || 'Not Assigned'}
                            {entry.sidRoleType === 'asid' && entry.sid?.sid?.nickname && (
                              <span className="text-gray-500"> ({entry.sid.sid.nickname})</span>
                            )}
                          </div>
                          {entry.sidName && entry.asidName && (
                            <div className="text-xs text-gray-500">
                              ASID: {entry.asidName}
                              {entry.asid?.sid?.nickname && ` (${entry.asid.sid.nickname})`}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {entry.siteSupervisorName || 'Not Assigned'}
                            {entry.siteManagerName && (
                              <span className="text-gray-500"> ({entry.siteManagerName})</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div className="w-24 bg-gray-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full ${statusConfig.progressColor}`}
                                style={{ width: `${Math.min(progress, 100)}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium text-gray-700 w-12">
                              {progress.toFixed(0)}%
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${statusConfig.color}`}>
                            {statusConfig.label}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-700">
                            {formatDate(entry.createdAt)}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="mt-4">
            <Pagination
              currentPage={currentPage}
              totalItems={filteredEntries.length}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </div>
        </>
      )}

      {/* Mobile Filter FAB */}
      {isMobile && (
        <button
          onClick={() => setIsFilterDrawerOpen(true)}
          className="fixed bottom-20 right-4 z-40 flex items-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 active:scale-95 transition-all"
          aria-label="Open filters"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          <span className="text-sm font-medium">Filters</span>
          {hasActiveFilters && (
            <span className="flex items-center justify-center w-5 h-5 bg-white text-blue-600 text-xs font-bold rounded-full">
              {activeFilterCount}
            </span>
          )}
        </button>
      )}

      {/* Mobile Filter Bottom Sheet */}
      <BottomSheet
        isOpen={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        title="Filters"
        maxHeight="85vh"
      >
        <div className="p-4 space-y-4">
          {/* Quick Filters Section */}
          <div className="space-y-3">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Quick Filters</h4>

            {/* Search */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Search</label>
              <input
                type="text"
                placeholder="Project number, client name, email..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full px-3 py-3 text-base border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Status filter */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Site Status</label>
              <select
                value={filters.siteStatus}
                onChange={(e) => handleFilterChange('siteStatus', e.target.value)}
                className="w-full px-3 py-3 text-base border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">All Statuses</option>
                {Object.entries(SITE_STATUS_CONFIG).map(([status, config]) => (
                  <option key={status} value={status}>{config.label}</option>
                ))}
              </select>
            </div>

            {/* Site Manager filter */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Site Manager</label>
              <select
                value={filters.siteManager}
                onChange={(e) => handleFilterChange('siteManager', e.target.value)}
                className="w-full px-3 py-3 text-base border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">All</option>
                <option value="assigned">Assigned (Any)</option>
                <option value="unassigned">Unassigned</option>
                {filterOptions.siteManagers.map(opt => (
                  <option key={opt.name} value={opt.name}>
                    {opt.isResigned ? `${opt.name} (resigned)` : opt.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Site Supervisor filter - CASCADE filtered */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Site Supervisor</label>
              <select
                value={filters.siteSupervisor}
                onChange={(e) => handleFilterChange('siteSupervisor', e.target.value)}
                className="w-full px-3 py-3 text-base border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">All</option>
                <option value="assigned">Assigned (Any)</option>
                <option value="unassigned">Unassigned</option>
                {filteredSupervisorOptions.map(opt => (
                  <option key={opt.name} value={opt.name}>
                    {opt.isResigned ? `${opt.name} (resigned)` : opt.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Advanced Filters Section */}
          <div className="space-y-3 pt-4 border-t border-gray-200">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Advanced Filters</h4>

            {/* Project Type - moved from quick filters */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Project Type</label>
              <select
                value={filters.projectType}
                onChange={(e) => handleFilterChange('projectType', e.target.value)}
                className="w-full px-3 py-3 text-base border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">All Types</option>
                <option value="renovation">Renovation</option>
                <option value="build_from_land">Build from Land</option>
              </select>
            </div>

            {/* Property Type */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Property Type</label>
              <select
                value={filters.propertyType}
                onChange={(e) => handleFilterChange('propertyType', e.target.value)}
                className="w-full px-3 py-3 text-base border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">All Property Types</option>
                {filterOptions.propertyTypes.map(type => (
                  <option key={type} value={type}>{formatPropertyType(type)}</option>
                ))}
              </select>
            </div>

            {/* City */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">City</label>
              <select
                value={filters.city}
                onChange={(e) => handleFilterChange('city', e.target.value)}
                className="w-full px-3 py-3 text-base border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">All Cities</option>
                {filterOptions.cities.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>

            {/* State */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">State</label>
              <select
                value={filters.state}
                onChange={(e) => handleFilterChange('state', e.target.value)}
                className="w-full px-3 py-3 text-base border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">All States</option>
                {filterOptions.states.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>

            {/* Price Range */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Min Price (RM)</label>
                <input
                  type="number"
                  inputMode="numeric"
                  placeholder="0"
                  value={filters.priceMin}
                  onChange={(e) => handleFilterChange('priceMin', e.target.value)}
                  className="w-full px-3 py-3 text-base border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Max Price (RM)</label>
                <input
                  type="number"
                  inputMode="numeric"
                  placeholder="No limit"
                  value={filters.priceMax}
                  onChange={(e) => handleFilterChange('priceMax', e.target.value)}
                  className="w-full px-3 py-3 text-base border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* SID/ASID Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">SID/ASID</label>
              <select
                value={filters.sidAssignment}
                onChange={(e) => handleFilterChange('sidAssignment', e.target.value)}
                className="w-full px-3 py-3 text-base border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">All</option>
                <option value="assigned">Assigned (Any)</option>
                <option value="unassigned">Unassigned</option>
                {filterOptions.sidNames.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>

            {/* Progress Range */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Min Progress (%)</label>
                <input
                  type="number"
                  inputMode="numeric"
                  placeholder="0"
                  min="0"
                  max="100"
                  value={filters.progressMin}
                  onChange={(e) => handleFilterChange('progressMin', e.target.value)}
                  className="w-full px-3 py-3 text-base border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Max Progress (%)</label>
                <input
                  type="number"
                  inputMode="numeric"
                  placeholder="100"
                  min="0"
                  max="100"
                  value={filters.progressMax}
                  onChange={(e) => handleFilterChange('progressMax', e.target.value)}
                  className="w-full px-3 py-3 text-base border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200 pb-safe">
            <button
              onClick={() => {
                clearFilters();
                setIsFilterDrawerOpen(false);
              }}
              className="flex-1 px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-colors"
            >
              Clear All
            </button>
            <button
              onClick={() => setIsFilterDrawerOpen(false)}
              className="flex-1 px-4 py-3 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </BottomSheet>

      {/* Add Site Entry Wizard */}
      <AddSiteEntryWizard
        isOpen={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onSuccess={() => {
          setWizardOpen(false);
          fetchEntries();
        }}
      />

      {/* Package Price Edit Modal (director access, data cleanup) */}
      <ResponsiveModal
        isOpen={!!priceEditEntry}
        onClose={closePriceEdit}
        title="Edit Package Price"
        size="sm"
      >
        {priceEditEntry && (
          <div className="p-4 space-y-4">
            <div className="text-sm text-gray-600">
              <div><span className="font-medium text-gray-900">{priceEditEntry.projectNumber}</span> &mdash; {toTitleCase(priceEditEntry.clientName)}</div>
              <div className="text-xs text-gray-500 mt-1">
                This updates the project's current package price everywhere it is used (contracts, payment milestones, quotations). Existing payment milestone amounts are not recalculated.
              </div>
            </div>

            <div>
              <label htmlFor="package-price-input" className="block text-xs font-medium text-gray-700 mb-1">
                Package Price (RM)
              </label>
              <input
                id="package-price-input"
                type="number"
                min="0"
                step="0.01"
                value={priceInput}
                onChange={(e) => setPriceInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !priceSaving) savePrice();
                }}
                disabled={priceSaving}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50"
                placeholder="0.00"
                autoFocus
              />
              {priceError && (
                <p className="mt-1 text-xs text-red-600">{priceError}</p>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={closePriceEdit}
                disabled={priceSaving}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={savePrice}
                disabled={priceSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {priceSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </ResponsiveModal>
    </div>
  );
}
